import { supabase } from '@/integrations/supabase/client';

export interface LegalDocument {
  id: string;
  document_type: 'privacy_policy' | 'terms_of_service';
  version: string;
  title: string;
  content: string;
  is_active: boolean;
  changelog: string | null;
  created_by: string | null;
  published_at: string | null;
  file_path: string | null;
  file_size: number | null;
}

export interface ActiveDocument {
  id: string;
  document_type: string;
  version: string;
  title: string;
  content: string;
  changelog: string | null;
}

export interface SemanticVersion {
  major: number;
  minor: number;
  patch: number;
}

const STORAGE_BUCKET = 'legal-documents';

// Version parsing and formatting helpers
export const parseVersion = (version: string): SemanticVersion => {
  const parts = version.split('.');
  return {
    major: parseInt(parts[0]) || 0,
    minor: parseInt(parts[1]) || 1,
    patch: parseInt(parts[2]) || 0
  };
};

export const formatVersion = (major: number, minor: number, patch: number): string => {
  return `${major}.${minor}.${patch}`;
};

export const incrementMajorVersion = (current: SemanticVersion): SemanticVersion => ({
  major: current.major + 1,
  minor: 0,
  patch: 0
});

export const incrementMinorVersion = (current: SemanticVersion): SemanticVersion => ({
  major: current.major,
  minor: current.minor + 1,
  patch: 0
});

export const legalDocumentService = {
  // Get active document by type (for public pages)
  async getActiveDocument(documentType: 'privacy_policy' | 'terms_of_service'): Promise<ActiveDocument | null> {
    const { data, error } = await supabase
      .rpc('get_active_legal_document', { p_document_type: documentType });

    if (error) {
      console.error('Error fetching active document:', error);
      return null;
    }

    // Map the RPC response
    const doc = data?.[0];
    if (!doc || typeof doc !== 'object') return null;
    
    const typedDoc = doc as Record<string, unknown>;
    return {
      id: String(typedDoc.id || ''),
      document_type: String(typedDoc.document_type || ''),
      version: String(typedDoc.version || ''),
      title: String(typedDoc.title || ''),
      content: String(typedDoc.content || ''),
      changelog: (typedDoc.change_summary as string) || (typedDoc.changelog as string) || null,
    };
  },

  // Get active document with file content from storage
  async getActiveDocumentWithContent(documentType: 'privacy_policy' | 'terms_of_service'): Promise<{ document: ActiveDocument | null; content: string | null }> {
    // First get the document metadata
    const { data: docData, error: docError } = await supabase
      .from('legal_documents')
      .select('*')
      .eq('document_type', documentType)
      .eq('is_active', true)
      .single();

    if (docError || !docData) {
      console.error('Error fetching active document:', docError);
      return { document: null, content: null };
    }

    // If file_path exists, fetch from storage
    if (docData.file_path) {
      const content = await this.getDocumentContent(docData.file_path);
      return { 
        document: docData as ActiveDocument, 
        content: content || docData.content // Fallback to DB content
      };
    }

    // Fallback to database content
    return { 
      document: docData as ActiveDocument, 
      content: docData.content 
    };
  },

  // Get all versions for a document type (admin only)
  async getAllVersions(documentType: 'privacy_policy' | 'terms_of_service'): Promise<LegalDocument[]> {
    const { data, error } = await supabase
      .from('legal_documents')
      .select('*')
      .eq('document_type', documentType)
      .order('published_at', { ascending: false, nullsFirst: false });

    if (error) {
      console.error('Error fetching document versions:', error);
      return [];
    }

    return data as LegalDocument[];
  },

  // Get a specific version (admin only)
  async getVersion(id: string): Promise<LegalDocument | null> {
    const { data, error } = await supabase
      .from('legal_documents')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching document version:', error);
      return null;
    }

    return data as LegalDocument;
  },

  // Upload document content to storage
  async uploadDocumentFile(
    documentType: 'privacy_policy' | 'terms_of_service',
    version: string,
    content: string
  ): Promise<{ success: boolean; filePath?: string; fileSize?: number; error?: string }> {
    // Generate file path: {type}/v{version}.md
    const typeFolder = documentType === 'privacy_policy' ? 'privacy-policy' : 'terms-of-service';
    const filePath = `${typeFolder}/v${version}.md`;
    
    // Convert content to Blob
    const blob = new Blob([content], { type: 'text/markdown' });
    const fileSize = blob.size;

    // Upload to storage (upsert to allow updates)
    const { error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(filePath, blob, { 
        upsert: true,
        contentType: 'text/markdown'
      });

    if (error) {
      console.error('Error uploading document:', error);
      return { success: false, error: error.message };
    }

    return { success: true, filePath, fileSize };
  },

  // Get document content from storage
  async getDocumentContent(filePath: string): Promise<string | null> {
    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .download(filePath);

    if (error) {
      console.error('Error downloading document:', error);
      return null;
    }

    return await data.text();
  },

  // Create new document version (admin only)
  async createDocument(document: {
    document_type: 'privacy_policy' | 'terms_of_service';
    version: string;
    title: string;
    content: string;
    changelog?: string;
  }): Promise<{ success: boolean; error?: string; id?: string }> {
    const { data: userData } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('legal_documents')
      .insert({
        document_type: document.document_type,
        version: document.version,
        title: document.title,
        content: document.content,
        changelog: document.changelog,
        created_by: userData?.user?.id,
        is_active: false,
      })
      .select('id')
      .single();

    if (error) {
      console.error('Error creating document:', error);
      return { success: false, error: error.message };
    }

    return { success: true, id: data.id };
  },

  // Update document (admin only)
  async updateDocument(id: string, updates: Partial<LegalDocument>): Promise<{ success: boolean; error?: string }> {
    // If content is being updated, upload to storage
    if (updates.content) {
      const doc = await this.getVersion(id);
      if (doc) {
        const uploadResult = await this.uploadDocumentFile(
          doc.document_type,
          doc.version,
          updates.content
        );
        
        if (uploadResult.success) {
          updates.file_path = uploadResult.filePath;
          updates.file_size = uploadResult.fileSize;
        }
      }
    }

    const { error } = await supabase
      .from('legal_documents')
      .update(updates)
      .eq('id', id);

    if (error) {
      console.error('Error updating document:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  },

  // Reset all users' terms acceptance (used when reconsent is required)
  async resetAllUsersTermsAcceptance(): Promise<{ success: boolean; error?: string; affectedCount?: number }> {
    // First count how many users will be affected
    const { count: affectedCount } = await supabase
      .from('profiles')
      .select('id', { count: 'exact', head: true })
      .eq('terms_accepted', true);

    // Then update all users
    const { error } = await supabase
      .from('profiles')
      .update({
        terms_accepted: false,
        terms_version: null
      })
      .eq('terms_accepted', true); // Only update users who have accepted

    if (error) {
      console.error('Error resetting terms acceptance:', error);
      return { success: false, error: error.message };
    }

    return { success: true, affectedCount: affectedCount || 0 };
  },

  // Publish document (set as active, archive previous active version)
  async publishDocument(
    id: string, 
    documentType: 'privacy_policy' | 'terms_of_service',
    requiresReconsent: boolean = false
  ): Promise<{ success: boolean; error?: string; affectedUsers?: number }> {
    // First, find and archive the currently active document
    const { data: activeDoc } = await supabase
      .from('legal_documents')
      .select('*')
      .eq('document_type', documentType)
      .eq('is_active', true)
      .single();

    if (activeDoc && activeDoc.id !== id) {
      // Archive the old active document: upload content to storage and clear DB content
      if (activeDoc.content && !activeDoc.file_path) {
        const uploadResult = await this.uploadDocumentFile(
          documentType,
          activeDoc.version,
          activeDoc.content
        );

        if (uploadResult.success) {
          // Update old doc: set file_path, clear content, deactivate
          await supabase
            .from('legal_documents')
            .update({
              is_active: false,
              file_path: uploadResult.filePath,
              file_size: uploadResult.fileSize,
              content: '' // Clear content since it's now in storage
            })
            .eq('id', activeDoc.id);
        } else {
          // Just deactivate if upload fails
          await supabase
            .from('legal_documents')
            .update({ is_active: false })
            .eq('id', activeDoc.id);
        }
      } else {
        // Just deactivate
        await supabase
          .from('legal_documents')
          .update({ is_active: false })
          .eq('id', activeDoc.id);
      }
    }

    // Deactivate any other active versions (safety)
    await supabase
      .from('legal_documents')
      .update({ is_active: false })
      .eq('document_type', documentType)
      .neq('id', id);

    // Activate the new version (keep content in DB for fast access)
    const { error: activateError } = await supabase
      .from('legal_documents')
      .update({ 
        is_active: true, 
        published_at: new Date().toISOString()
      })
      .eq('id', id);

    if (activateError) {
      console.error('Error activating document:', activateError);
      return { success: false, error: activateError.message };
    }

    // If reconsent is required, reset all users' terms acceptance
    let affectedUsers = 0;
    if (requiresReconsent) {
      const resetResult = await this.resetAllUsersTermsAcceptance();
      if (!resetResult.success) {
        console.error('Failed to reset user terms acceptance:', resetResult.error);
        // Don't fail the publish, but log the error
      } else {
        affectedUsers = resetResult.affectedCount || 0;
      }
    }

    return { success: true, affectedUsers };
  },

  // Delete document (admin only, cannot delete active)
  async deleteDocument(id: string): Promise<{ success: boolean; error?: string }> {
    // Check if document is active
    const doc = await this.getVersion(id);

    if (doc?.is_active) {
      return { success: false, error: 'Cannot delete active document' };
    }

    // Delete from storage if file exists
    if (doc?.file_path) {
      await supabase.storage
        .from(STORAGE_BUCKET)
        .remove([doc.file_path]);
    }

    const { error } = await supabase
      .from('legal_documents')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting document:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  },

  // Restore a previous version as a new draft
  async restoreAsDraft(id: string): Promise<{ success: boolean; error?: string; newId?: string }> {
    const doc = await this.getVersion(id);
    if (!doc) {
      return { success: false, error: 'Document not found' };
    }

    // Get content from storage or fallback to DB
    let content = doc.content;
    if (doc.file_path) {
      const storedContent = await this.getDocumentContent(doc.file_path);
      if (storedContent) {
        content = storedContent;
      }
    }

    // Create new version with incremented patch using parseVersion()
    const currentVersion = parseVersion(doc.version);
    const newPatch = currentVersion.patch + 1;
    const newVersion = formatVersion(currentVersion.major, currentVersion.minor, newPatch);
    
    const result = await this.createDocument({
      document_type: doc.document_type,
      version: newVersion,
      title: doc.title,
      content,
      changelog: `Restored from version ${doc.version}`,
    });

    return result.success 
      ? { success: true, newId: result.id }
      : { success: false, error: result.error };
  },


  // Get latest version string for a document type
  async getLatestVersion(documentType: 'privacy_policy' | 'terms_of_service'): Promise<string | null> {
    const { data, error } = await supabase
      .from('legal_documents')
      .select('version')
      .eq('document_type', documentType)
      .eq('is_active', true)
      .single();

    if (error) {
      console.error('Error fetching latest version:', error);
      return null;
    }

    return data?.version || null;
  },

  // Migrate existing content to storage (one-time migration tool)
  async migrateContentToStorage(id: string): Promise<{ success: boolean; error?: string }> {
    const doc = await this.getVersion(id);
    if (!doc) {
      return { success: false, error: 'Document not found' };
    }

    if (doc.file_path) {
      return { success: true }; // Already migrated
    }

    if (!doc.content) {
      return { success: false, error: 'No content to migrate' };
    }

    const uploadResult = await this.uploadDocumentFile(
      doc.document_type,
      doc.version,
      doc.content
    );

    if (!uploadResult.success) {
      return { success: false, error: uploadResult.error };
    }

    // Update document with file path
    const { error } = await supabase
      .from('legal_documents')
      .update({
        file_path: uploadResult.filePath,
        file_size: uploadResult.fileSize
      })
      .eq('id', id);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  }
};
